// Карточка отсчёта: координаты точки и всё, что о ней известно.
//
// Всегда ЗАКРЕПЛЁННАЯ и ловит мышь — в отличие от читалок судна и
// землетрясения. Причина простая: карточка держит запрос в сеть, значит
// открывается по клику, а раз по клику — её надо закрывать крестиком, и
// прозрачная для мыши она бы этого не позволила.
//
// Значения принадлежат УЗЛУ, а не пикселю под пальцем, поэтому координаты узла
// и расстояние до него стоят отдельной строкой. Без неё карточка выдавала бы
// величину соседней ячейки за величину под курсором.
//
// Где карточка встаёт и почему она не накрывает саму точку — `MapCallout`.
// Фон НЕПРОЗРАЧНЫЙ (оттуда же), в отличие от легенд и отсчёта координат: под
// карточкой лежит растр слоя, и сквозь щель в 8 % над жёлтым пятном HSI
// приходило столько света, что подписи цвета caption переставали читаться.
// Сторож — `brand/textContrast.test.ts`.
import { Icon } from '../brand/Icon'
import { getLang, useT } from '../i18n'
import { substrateClassKey, substrateLine, type SubstrateProps } from './substrate'
import { MapCallout } from './MapCallout'
import { pointCardView, type PointProbe } from './pointCardView'
import { Link } from 'react-router-dom'
import { rulesHereHref } from '../rules/rulesUrl'

interface Props {
  /** Точка, о которой карточка рассказывает: к ней она и привязана. */
  anchor: { lng: number; lat: number }
  probe: PointProbe | null
  loading: boolean
  failed: boolean
  onClose: () => void
  /** Полигон карты дна под точкой (оверлей «Тип дна»); нет слоя — нет строки. */
  substrate?: SubstrateProps | null
}

/** Ширина карточки. */
const WIDTH = 244

export function PointCard({ anchor, probe, loading, failed, onClose, substrate }: Props) {
  const t = useT()
  const view = probe ? pointCardView(probe, getLang()) : null

  return (
    <MapCallout anchor={anchor} width={WIDTH}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-foam">
          <Icon name="ic-point" className="h-4 w-4 text-sonar" /> {t('maps.point.title')}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-mist transition-colors hover:bg-haze hover:text-foam"
        >
          ×
        </button>
      </div>

      {/* Координаты — ВСЕГДА, даже пока значения едут: их мы знаем сразу, и
          ради них карточку часто и открывают. */}
      <div className="tnum text-[12px] font-medium leading-snug text-foam">
        {view ? view.coords : t('maps.point.loading')}
      </div>

      {view?.node && (
        <div className="tnum mt-0.5 text-[11px] leading-snug text-faint">
          {t('maps.point.node')} {view.node.coords} · {view.node.distance}
        </div>
      )}
      {view && !view.node && (
        <div className="mt-0.5 text-[11px] leading-snug text-faint">{t('maps.point.noNode')}</div>
      )}

      {/* В чьей зоне точка. Стоит рядом с координатами, а не в списке значений:
          у зоны нет ни единицы, ни горизонта, и «нет данных» на её месте
          означало бы совсем другое, чем у измерения. */}
      {view?.zone && (
        <div className="mt-1 text-[11px] leading-snug text-mist">
          <span className="text-faint">{t('eez.zone.title')}: </span>
          {view.zone}
        </div>
      )}

      {/* Промысловый район — ДРУГОЙ вопрос, чем «в чьей воде»: по нему выдана
          квота и подаётся донесение. Своей строкой, а не припиской к зоне:
          их путают и без нашей помощи. */}
      {view?.district && (
        <div className="mt-1 text-[11px] leading-snug text-mist">
          <span className="text-faint">{t('districts.card.title')}: </span>
          {view.district}
        </div>
      )}

      {/* Тип дна — по самой карте ГГК-1000: код, подпись легенды, класс модели
          и лист. Ради этой строки оверлей и заведён — проверить, откуда класс. */}
      {substrate && (
        <div className="mt-1 text-[11px] leading-snug text-mist">
          <span className="text-faint">{t('layers.substrate')}: </span>
          {substrateLine(substrate, t(substrateClassKey(substrate.class5)), t('substrate.sheet'))}
        </div>
      )}

      {/* Запреты под точкой — «можно ли здесь ловить сегодня и этим видом».
          Статус стоит первым в строке: ради него карточку и открывают. */}
      {view?.bans && (
        <div className="mt-1 text-[11px] leading-snug text-mist">
          <span className="text-faint">{t('bans.card.title')}: </span>
          {view.bans.length === 1 ? (
            view.bans[0]
          ) : (
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4">
              {view.bans.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Из точки — в раздел «Правила»: все нормы, чьё место здесь, плюс
          нормы без места и места без координат. */}
      {view?.bans && (
        <Link to={rulesHereHref(anchor.lat, anchor.lng)}
          className="mt-0.5 inline-block text-[11px] text-mist underline hover:text-foam">
          {t('maps.point.rulesHere')}
        </Link>
      )}

      {loading && <div className="mt-2 text-[11px] text-faint">{t('maps.point.loading')}</div>}
      {failed && <div className="mt-2 text-[11px] text-buoy">{t('maps.point.failed')}</div>}

      {view && (
        <dl className="mt-2 space-y-1 text-[12px]">
          {view.rows.map(row => (
            <div key={row.id} className="flex items-baseline justify-between gap-2">
              {/* Подпись строки — не «ось графика», а body-текст: её читают, а
                  не скользят по ней. Отсюда `mist`, не `faint`. */}
              <dt className="shrink-0 text-mist">
                {row.label}
                {/* Поверхностная величина на глубинной карточке помечается, иначе
                    «Ветер» под заголовком «30 м» читался бы как ветер на глубине. */}
                {row.surface && (
                  <span className="ml-1 text-[10px] text-mist">{t('maps.point.surface')}</span>
                )}
              </dt>
              <dd
                className={`tnum min-w-0 truncate text-right font-medium ${
                  row.empty ? 'text-faint' : 'text-foam'
                }`}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </MapCallout>
  )
}
